import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const airportLabel = (airport) => {
  const code = airport.icao_code || airport.iata_code || airport.ident || `ID ${airport.id}`;
  const place = airport.municipality ? ` - ${airport.municipality}` : '';
  return `${code} - ${airport.name || 'Unnamed Airport'}${place}`;
};

function RequestCharterPage() {
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formState, setFormState] = useState({
    departure: '',
    destination: '',
    passengers: '',
    departureDate: '',
    notes: '',
  });

  useEffect(() => {
    let isMounted = true;

    const loadAirports = async () => {
      try {
        const response = await axios.get('/airports');
        if (isMounted) {
          setAirports(response.data);
          setError('');
        }
      } catch {
        if (isMounted) {
          setError('Unable to load airport data right now.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadAirports();
    return () => {
      isMounted = false;
    };
  }, []);

  const airportOptions = useMemo(() => airports.map((airport) => airportLabel(airport)), [airports]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  return (
    <div className="inner-page">
      <section className="inner-page-hero" style={{ '--inner-hero-image': "url('/assets/request-charter.jpg')" }}>
        <p className="inner-page-eyebrow">Private Charter Planning</p>
        <h1>Request a Charter</h1>
        <p>Plan your route using our full database of registered airports across South Africa.</p>
      </section>

      <section className="page-shell">
        <div className="panel-card">
          {loading && <p className="status-note">Loading airport database...</p>}
          {!loading && error && <p className="status-note status-note--error">{error}</p>}

          {!loading && !error && (
            <>
              <p className="status-note status-note--success">{airports.length} airports loaded</p>
              <form className="charter-form" onSubmit={handleSubmit}>
                <label className="form-field">
                  <span>Departure Airport</span>
                  <input
                    list="registered-airports"
                    name="departure"
                    value={formState.departure}
                    onChange={handleChange}
                    placeholder="Start typing airport code or name"
                    required
                  />
                </label>

                <label className="form-field">
                  <span>Destination Airport</span>
                  <input
                    list="registered-airports"
                    name="destination"
                    value={formState.destination}
                    onChange={handleChange}
                    placeholder="Start typing airport code or name"
                    required
                  />
                </label>

                <label className="form-field">
                  <span>Departure Date</span>
                  <input type="date" name="departureDate" value={formState.departureDate} onChange={handleChange} />
                </label>

                <label className="form-field">
                  <span>Passengers</span>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    name="passengers"
                    value={formState.passengers}
                    onChange={handleChange}
                    placeholder="e.g. 4"
                  />
                </label>

                <label className="form-field form-field--wide">
                  <span>Trip Notes</span>
                  <textarea
                    name="notes"
                    value={formState.notes}
                    onChange={handleChange}
                    placeholder="Any requirements, preferred aircraft class, or timing details"
                    rows={4}
                  />
                </label>

                <button className="primary-button" type="submit">
                  Submit Request
                </button>
              </form>

              <datalist id="registered-airports">
                {airportOptions.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default RequestCharterPage;
